import { Button } from "frontend-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/sharp-regular-svg-icons";
import { faArrowDown, faArrowUp, faTrashCan, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useImperativeHandle, useMemo, useState, type Ref } from "react";
import { useReorderAnimation, type ReorderAnimationId } from "frontend-components";

import type { TaskListWithTaskOccurrences } from "@dashboard/api/task";
import { ConfirmDeleteDialog } from "@dashboard/components/common/ConfirmDeleteDialog";
import {
    useListFormSetup,
    VALIDATION_LIMITS_LISTS,
} from "@dashboard/features/Dashboard/Lists/ListForm/formHooks";
import { VALIDATION_LIMITS } from "@dashboard/features/Dashboard/AnnualCycle/TaskForm/formHooks";
import { getListTaskCounts, type TaskFormValues } from "@dashboard/features/Dashboard/Lists/utils";

import { useListDeletions } from "./useListDeletions";
import { ListImagesSection } from "./ListImagesSection";

export interface ListFormBodyHandle {
    isDirty: () => boolean;
}

interface ListFormBodyProps {
    list: TaskListWithTaskOccurrences | null;
    source?: TaskListWithTaskOccurrences;
    template?: boolean;
    ref?: Ref<ListFormBodyHandle>;
    onCancel: () => void;
    onSaved: (listId?: number) => void;
}

let nextTaskClientId = 0;

function createTaskClientId() {
    nextTaskClientId += 1;
    return `new-task-${Date.now()}-${nextTaskClientId}`;
}

function getTaskAnimationId(task: TaskFormValues, index: number): ReorderAnimationId {
    return task.clientId ?? (task.id ? `task-${task.id}` : `task-index-${index}`);
}

export function ListFormBody({
    list,
    source,
    template = false,
    ref,
    onCancel,
    onSaved,
}: ListFormBodyProps) {
    const { t } = useTranslation();
    const { useListForm, withForm, formOpts, mapListToForm, mapListToCopyForm, handleListSubmit } =
        useListFormSetup();
    const deletions = useListDeletions();

    const isEditMode = !!list;
    const { completedTasks, totalTasks } = list
        ? getListTaskCounts(list)
        : { completedTasks: 0, totalTasks: 0 };
    const isCompleted = totalTasks > 0 && completedTasks === totalTasks;
    const progressPercent = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    const [taskToConfirm, setTaskToConfirm] = useState<{
        taskId: number;
        groupId?: number;
    } | null>(null);
    const [groupToConfirm, setGroupToConfirm] = useState<{
        groupId: number;
        taskCount: number;
    } | null>(null);
    const [imageToConfirm, setImageToConfirm] = useState<{
        imageId: number;
        taskId: number;
    } | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const defaultValues = useMemo(() => {
        if (list) return mapListToForm(list);
        if (source) return mapListToCopyForm(source);
        return { ...formOpts.defaultValues, template };
    }, [list, source, template, mapListToForm, mapListToCopyForm, formOpts.defaultValues]);

    const form = useListForm({
        ...formOpts,
        defaultValues,
        onSubmit: async ({ value }) => {
            setSubmitError(null);

            const flushResult = await deletions.flush();
            if (!flushResult.ok) {
                setSubmitError(t("common.errorDeletingItems"));
                return;
            }

            const existingId = list?.id;
            const hasFormChanges = !form.state.isDefaultValue;

            if (hasFormChanges || !existingId) {
                const result = await handleListSubmit(value, existingId, list ?? undefined);
                if (!result.success) {
                    setSubmitError(t("common.errorSavingList"));
                    return;
                }
            }

            // Update form defaults to the just-saved values so the post-save
            // navigation isn't blocked by useBlocker.
            form.reset(value);
            onSaved(existingId);
        },
    });

    useImperativeHandle(
        ref,
        () => ({
            isDirty: () => !form.state.isDefaultValue || deletions.hasQueued(),
        }),
        [form, deletions],
    );

    const ListForm = useMemo(
        () =>
            withForm({
                ...formOpts,
                render: function Render({ form }) {
                    const { t } = useTranslation();

                    return (
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                void form.handleSubmit();
                            }}
                            className="flex flex-col flex-1 min-h-0 text-compendia-foreground"
                        >
                            <div className="-mx-6 -mb-6 md:flex-1 md:min-h-0 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_320px]">
                                <div className="flex flex-col gap-4 p-6 md:overflow-y-auto md:min-h-0">
                                    <form.AppField
                                        name="title"
                                        children={(field) => (
                                            <field.Input
                                                label={t("common.title")}
                                                placeholder={t("list.whatIsThisListAbout") + "?"}
                                                maxLength={VALIDATION_LIMITS_LISTS.LIST_TITLE_MAX}
                                                showCharCount={true}
                                            />
                                        )}
                                    />
                                    <form.AppField
                                        name="tasks"
                                        mode="array"
                                        children={(field) => (
                                            <div className="flex flex-col gap-3">
                                                {field.state.value.length > 0 && (
                                                    <h4 className="text-label font-medium uppercase tracking-wider text-text-secondary">
                                                        {t("common.tasks")}
                                                    </h4>
                                                )}
                                                <ReorderableTaskList
                                                    tasks={field.state.value}
                                                    onReorder={field.moveValue}
                                                    renderTask={(task, index) => {
                                                        const taskId = task.id;
                                                        const existingTask = taskId
                                                            ? list?.tasks?.find(
                                                                  (t) => t.id === taskId,
                                                              )
                                                            : undefined;
                                                        return (
                                                            <TaskRow
                                                                existingTaskId={existingTask?.id}
                                                                fieldNode={
                                                                    <form.AppField
                                                                        name={`tasks[${index}].title`}
                                                                        children={(taskField) => (
                                                                            <taskField.Input
                                                                                placeholder={t(
                                                                                    "common.task",
                                                                                )}
                                                                                maxLength={
                                                                                    VALIDATION_LIMITS.TITLE_MAX_LENGTH
                                                                                }
                                                                                showCharCount={true}
                                                                            />
                                                                        )}
                                                                    />
                                                                }
                                                                imagesContent={
                                                                    existingTask?.id ? (
                                                                        <ListImagesSection
                                                                            taskId={existingTask.id}
                                                                            deletedImages={
                                                                                deletions.state
                                                                                    .images
                                                                            }
                                                                            onDeleteImage={(
                                                                                imageId,
                                                                            ) =>
                                                                                setImageToConfirm({
                                                                                    imageId,
                                                                                    taskId: existingTask.id,
                                                                                })
                                                                            }
                                                                        />
                                                                    ) : null
                                                                }
                                                                onRemove={() => {
                                                                    if (existingTask?.id) {
                                                                        setTaskToConfirm({
                                                                            taskId: existingTask.id,
                                                                        });
                                                                    } else {
                                                                        form.removeFieldValue(
                                                                            `tasks`,
                                                                            index,
                                                                        );
                                                                    }
                                                                }}
                                                                removeAriaLabel={t(
                                                                    "common.deleteTask",
                                                                )}
                                                            />
                                                        );
                                                    }}
                                                />
                                                <AddTaskButton
                                                    label={t("list.newTask")}
                                                    onClick={() =>
                                                        field.pushValue({
                                                            clientId: createTaskClientId(),
                                                            title: "",
                                                        })
                                                    }
                                                />
                                            </div>
                                        )}
                                    />

                                    <form.AppField
                                        name="groups"
                                        mode="array"
                                        children={(field) => (
                                            <div className="flex flex-col gap-4">
                                                {field.state.value.map((group, index) => {
                                                    const groupId = group.id;
                                                    const existingGroup = groupId
                                                        ? list?.groups?.find(
                                                              (g) => g.id === groupId,
                                                          )
                                                        : undefined;
                                                    return (
                                                        <GroupBlock
                                                            key={group.id ?? `new-${index}`}
                                                            existingGroupId={existingGroup?.id}
                                                            renderTitle={() => (
                                                                <form.AppField
                                                                    name={`groups[${index}].title`}
                                                                    children={(subfield) => (
                                                                        <subfield.Input
                                                                            label={t(
                                                                                "common.group_name",
                                                                            )}
                                                                            maxLength={
                                                                                VALIDATION_LIMITS_LISTS.GROUP_TITLE_MAX
                                                                            }
                                                                            showCharCount={true}
                                                                        />
                                                                    )}
                                                                />
                                                            )}
                                                            onRemoveGroup={() => {
                                                                if (existingGroup?.id) {
                                                                    setGroupToConfirm({
                                                                        groupId: existingGroup.id,
                                                                        taskCount:
                                                                            existingGroup.tasks
                                                                                ?.length || 0,
                                                                    });
                                                                } else {
                                                                    form.removeFieldValue(
                                                                        `groups`,
                                                                        index,
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            <form.AppField
                                                                name={`groups[${index}].tasks`}
                                                                mode="array"
                                                                children={(taskField) => (
                                                                    <div className="flex flex-col gap-3">
                                                                        <ReorderableTaskList
                                                                            tasks={
                                                                                taskField.state
                                                                                    .value
                                                                            }
                                                                            onReorder={
                                                                                taskField.moveValue
                                                                            }
                                                                            renderTask={(
                                                                                groupTask,
                                                                                taskIndex,
                                                                            ) => {
                                                                                const groupTaskId =
                                                                                    groupTask.id;
                                                                                const existingGroupTask =
                                                                                    groupTaskId
                                                                                        ? existingGroup?.tasks?.find(
                                                                                              (t) =>
                                                                                                  t.id ===
                                                                                                  groupTaskId,
                                                                                          )
                                                                                        : undefined;
                                                                                return (
                                                                                    <TaskRow
                                                                                        existingTaskId={
                                                                                            existingGroupTask?.id
                                                                                        }
                                                                                        fieldNode={
                                                                                            <form.AppField
                                                                                                name={`groups[${index}].tasks[${taskIndex}].title`}
                                                                                                children={(
                                                                                                    taskSubfield,
                                                                                                ) => (
                                                                                                    <taskSubfield.Input
                                                                                                        placeholder={t(
                                                                                                            "common.task",
                                                                                                        )}
                                                                                                        maxLength={
                                                                                                            VALIDATION_LIMITS.TITLE_MAX_LENGTH
                                                                                                        }
                                                                                                        showCharCount={
                                                                                                            true
                                                                                                        }
                                                                                                    />
                                                                                                )}
                                                                                            />
                                                                                        }
                                                                                        imagesContent={
                                                                                            existingGroupTask?.id ? (
                                                                                                <ListImagesSection
                                                                                                    taskId={
                                                                                                        existingGroupTask.id
                                                                                                    }
                                                                                                    deletedImages={
                                                                                                        deletions
                                                                                                            .state
                                                                                                            .images
                                                                                                    }
                                                                                                    onDeleteImage={(
                                                                                                        imageId,
                                                                                                    ) =>
                                                                                                        setImageToConfirm(
                                                                                                            {
                                                                                                                imageId,
                                                                                                                taskId: existingGroupTask.id,
                                                                                                            },
                                                                                                        )
                                                                                                    }
                                                                                                />
                                                                                            ) : null
                                                                                        }
                                                                                        onRemove={() => {
                                                                                            if (
                                                                                                existingGroupTask?.id
                                                                                            ) {
                                                                                                setTaskToConfirm(
                                                                                                    {
                                                                                                        taskId: existingGroupTask.id,
                                                                                                        groupId:
                                                                                                            existingGroup?.id,
                                                                                                    },
                                                                                                );
                                                                                            } else {
                                                                                                form.removeFieldValue(
                                                                                                    `groups[${index}].tasks`,
                                                                                                    taskIndex,
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                        removeAriaLabel={t(
                                                                                            "common.deleteTask",
                                                                                        )}
                                                                                    />
                                                                                );
                                                                            }}
                                                                        />
                                                                        <AddTaskButton
                                                                            label={t(
                                                                                "list.newTask",
                                                                            )}
                                                                            onClick={() =>
                                                                                taskField.pushValue(
                                                                                    {
                                                                                        clientId:
                                                                                            createTaskClientId(),
                                                                                        title: "",
                                                                                    },
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>
                                                                )}
                                                            />
                                                        </GroupBlock>
                                                    );
                                                })}
                                                <AddGroupButton
                                                    label={t("list.newGroup")}
                                                    onClick={() =>
                                                        field.pushValue({
                                                            title: "",
                                                            tasks: [],
                                                        })
                                                    }
                                                />
                                            </div>
                                        )}
                                    />
                                </div>

                                <FormDetailSidebar
                                    actions={
                                        <>
                                            {submitError && (
                                                <p
                                                    role="alert"
                                                    className="text-chameleon-pink text-xs flex-1"
                                                >
                                                    {submitError}
                                                </p>
                                            )}
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={onCancel}
                                            >
                                                {t("common.cancel")}
                                            </Button>
                                            <form.Subscribe
                                                selector={(state) => [state.isSubmitting]}
                                            >
                                                {([isSubmitting]) => (
                                                    <Button
                                                        type="submit"
                                                        variant="submit"
                                                        size="submit"
                                                        disabled={isSubmitting}
                                                    >
                                                        {isEditMode
                                                            ? t("common.save")
                                                            : t("common.create")}
                                                    </Button>
                                                )}
                                            </form.Subscribe>
                                        </>
                                    }
                                >
                                    {isEditMode && (
                                        <FormProgressSection
                                            isCompleted={isCompleted}
                                            progressPercent={progressPercent}
                                            completedTasks={completedTasks}
                                            totalTasks={totalTasks}
                                        />
                                    )}
                                    <form.AppField
                                        name="description"
                                        children={(field) => (
                                            <field.TextArea
                                                label={t("common.description")}
                                                maxLength={
                                                    VALIDATION_LIMITS_LISTS.LIST_DESCRIPTION_MAX
                                                }
                                                showCharCount={true}
                                                placeholder={
                                                    t("list.briefDescriptionOfTheList") + "?"
                                                }
                                                className="min-h-[72px] md:min-h-[120px]"
                                            />
                                        )}
                                    />
                                    <form.AppField
                                        name="template"
                                        children={(field) => (
                                            <field.Checkbox label={t("list.template")} />
                                        )}
                                    />
                                    <form.AppField
                                        name="responsibleUserId"
                                        children={(field) => (
                                            <field.UserSelect
                                                label={t("common.responsible")}
                                                placeholder={
                                                    t("common.whoIsResponsibleForThisActivity") +
                                                    "?"
                                                }
                                            />
                                        )}
                                    />
                                </FormDetailSidebar>
                            </div>
                        </form>
                    );
                },
            }),

        [
            withForm,
            formOpts,
            list,
            deletions.state.images,
            isEditMode,
            onCancel,
            isCompleted,
            progressPercent,
            completedTasks,
            totalTasks,
            submitError,
        ],
    );

    return (
        <div className="flex-1 min-h-0 flex flex-col">
            <ListForm form={form} />
            <ConfirmDeleteDialog
                open={taskToConfirm !== null}
                onOpenChange={(open) => {
                    if (!open) setTaskToConfirm(null);
                }}
                onConfirm={async () => {
                    if (taskToConfirm !== null) {
                        deletions.queueTask(taskToConfirm.taskId);
                        const currentFormState = form.state.values;

                        if (taskToConfirm.groupId !== undefined) {
                            const groupIndex = currentFormState.groups.findIndex(
                                (g) => g.id === taskToConfirm.groupId,
                            );
                            if (groupIndex !== -1) {
                                const taskIndex = currentFormState.groups[
                                    groupIndex
                                ].tasks.findIndex((t) => t.id === taskToConfirm.taskId);
                                if (taskIndex !== -1) {
                                    form.removeFieldValue(`groups[${groupIndex}].tasks`, taskIndex);
                                }
                            }
                        } else {
                            const taskIndex = currentFormState.tasks.findIndex(
                                (t) => t.id === taskToConfirm.taskId,
                            );
                            if (taskIndex !== -1) {
                                form.removeFieldValue(`tasks`, taskIndex);
                            }
                        }
                        setTaskToConfirm(null);
                    }
                }}
                title={t("common.deleteTask")}
                description={t("common.confirmDeleteTask")}
            />
            <ConfirmDeleteDialog
                open={groupToConfirm !== null}
                onOpenChange={(open) => {
                    if (!open) setGroupToConfirm(null);
                }}
                onConfirm={async () => {
                    if (groupToConfirm !== null) {
                        deletions.queueGroup(groupToConfirm.groupId);

                        const groupToDelete = list?.groups?.find(
                            (g) => g.id === groupToConfirm.groupId,
                        );
                        if (groupToDelete?.tasks) {
                            const taskIdsToDelete = groupToDelete.tasks
                                .filter((task) => task.id)
                                .map((task) => task.id);
                            taskIdsToDelete.forEach((taskId) => deletions.queueTask(taskId));
                        }

                        const currentFormState = form.state.values;
                        const groupIndex = currentFormState.groups.findIndex(
                            (g) => g.id === groupToConfirm.groupId,
                        );
                        if (groupIndex !== -1) {
                            form.removeFieldValue(`groups`, groupIndex);
                        }
                        setGroupToConfirm(null);
                    }
                }}
                title={t("common.deleteGroup")}
                description={
                    groupToConfirm && groupToConfirm.taskCount > 0
                        ? t("common.confirmDeleteGroupWithTasks", {
                              count: groupToConfirm.taskCount,
                          })
                        : t("common.confirmDeleteGroup")
                }
            />
            <ConfirmDeleteDialog
                open={imageToConfirm !== null}
                onOpenChange={(open) => {
                    if (!open) setImageToConfirm(null);
                }}
                onConfirm={async () => {
                    if (imageToConfirm !== null) {
                        deletions.queueImage(imageToConfirm.imageId, imageToConfirm.taskId);
                        setImageToConfirm(null);
                    }
                }}
                title={t("list.deleteImage")}
                description={t("list.confirmDeleteImage")}
            />
        </div>
    );
}

function TaskRow({
    existingTaskId,
    fieldNode,
    imagesContent,
    onRemove,
    removeAriaLabel,
}: {
    existingTaskId: number | undefined;
    fieldNode: React.ReactNode;
    imagesContent: React.ReactNode;
    onRemove: () => void;
    removeAriaLabel: string;
}) {
    return (
        <div className="bg-compendia-card border border-compendia-border rounded-lg p-3 flex flex-col gap-2">
            <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">{fieldNode}</div>
                <button
                    type="button"
                    onClick={onRemove}
                    aria-label={removeAriaLabel}
                    className={`size-8 inline-flex items-center justify-center rounded-full cursor-pointer ${
                        existingTaskId
                            ? "text-chameleon-pink hover:bg-chameleon-pink/10"
                            : "text-compendia-muted hover:bg-compendia-greige-20"
                    }`}
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            </div>
            {imagesContent}
        </div>
    );
}

function ReorderableTaskRow({
    index,
    total,
    children,
    itemRef,
    onMove,
}: {
    index: number;
    total: number;
    children: React.ReactNode;
    itemRef: (node: HTMLDivElement | null) => void;
    onMove: (fromIndex: number, toIndex: number) => void;
}) {
    const { t } = useTranslation();

    return (
        <div ref={itemRef} className="flex items-start gap-2">
            <div
                className="flex shrink-0 flex-col items-center gap-1 pt-3"
                onMouseDown={(event) => event.stopPropagation()}
                onMouseUp={(event) => event.stopPropagation()}
            >
                <button
                    type="button"
                    className="flex size-8 items-center justify-center rounded-full text-compendia-muted hover:bg-compendia-greige-20 hover:text-compendia-foreground disabled:opacity-30"
                    aria-label={t("common.moveTaskUp")}
                    disabled={index === 0}
                    onClick={() => onMove(index, index - 1)}
                >
                    <FontAwesomeIcon icon={faArrowUp} />
                </button>
                <button
                    type="button"
                    className="flex size-8 items-center justify-center rounded-full text-compendia-muted hover:bg-compendia-greige-20 hover:text-compendia-foreground disabled:opacity-30"
                    aria-label={t("common.moveTaskDown")}
                    disabled={index === total - 1}
                    onClick={() => onMove(index, index + 1)}
                >
                    <FontAwesomeIcon icon={faArrowDown} />
                </button>
            </div>
            <div className="min-w-0 flex-1">{children}</div>
        </div>
    );
}

function ReorderableTaskList({
    tasks,
    onReorder,
    renderTask,
}: {
    tasks: TaskFormValues[];
    onReorder: (fromIndex: number, toIndex: number) => void;
    renderTask: (task: TaskFormValues, index: number) => React.ReactNode;
}) {
    const itemIds = tasks.map(getTaskAnimationId);
    const { capturePositions, setItemRef } = useReorderAnimation(itemIds);

    const moveTask = (fromIndex: number, toIndex: number) => {
        if (toIndex < 0 || toIndex >= tasks.length || fromIndex === toIndex) {
            return;
        }

        capturePositions();
        onReorder(fromIndex, toIndex);
    };

    return (
        <div className="flex flex-col gap-3">
            {tasks.map((task, index) => (
                <ReorderableTaskRow
                    key={getTaskAnimationId(task, index)}
                    index={index}
                    total={tasks.length}
                    itemRef={setItemRef(getTaskAnimationId(task, index))}
                    onMove={moveTask}
                >
                    {renderTask(task, index)}
                </ReorderableTaskRow>
            ))}
        </div>
    );
}

function GroupBlock({
    existingGroupId,
    renderTitle,
    onRemoveGroup,
    children,
}: {
    existingGroupId: number | undefined;
    renderTitle: () => React.ReactNode;
    onRemoveGroup: () => void;
    children: React.ReactNode;
}) {
    const { t } = useTranslation();
    return (
        <div className="bg-compendia-card border border-compendia-border rounded-lg p-4 flex flex-col gap-3">
            <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">{renderTitle()}</div>
                <button
                    type="button"
                    onClick={onRemoveGroup}
                    aria-label={t("common.deleteGroup")}
                    className={`size-8 inline-flex items-center justify-center rounded-full cursor-pointer mt-7 ${
                        existingGroupId
                            ? "text-chameleon-pink hover:bg-chameleon-pink/10"
                            : "text-compendia-muted hover:bg-compendia-greige-20"
                    }`}
                >
                    <FontAwesomeIcon icon={faTrashCan} />
                </button>
            </div>
            {children}
        </div>
    );
}

function AddTaskButton({ label, onClick }: { label: string; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="w-full flex items-center justify-center gap-2 py-3 text-sm text-compendia-foreground border border-dashed border-compendia-border rounded-lg hover:border-chameleon-green cursor-pointer transition-colors"
        >
            <FontAwesomeIcon icon={faPlus} className="text-xs" />
            {label}
        </button>
    );
}

function AddGroupButton({ label, onClick }: { label: string; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="w-full flex items-center justify-center gap-2 py-3 text-sm text-chameleon-pink border border-dashed border-chameleon-pink bg-chameleon-pink/10 rounded-lg hover:bg-chameleon-pink/20 cursor-pointer transition-colors"
        >
            <FontAwesomeIcon icon={faPlus} className="text-xs" />
            {label}
        </button>
    );
}

function FormProgressSection({
    isCompleted,
    progressPercent,
    completedTasks,
    totalTasks,
}: {
    isCompleted: boolean;
    progressPercent: number;
    completedTasks: number;
    totalTasks: number;
}) {
    const { t } = useTranslation();
    const percentClass = isCompleted ? "text-chameleon-green" : "text-compendia-foreground";

    return (
        <section className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
                <span className="text-label uppercase tracking-wider text-compendia-muted">
                    {t("common.progress")}
                </span>
                <span className={`text-sm font-semibold ${percentClass}`}>{progressPercent}%</span>
            </div>
            <div className="h-1.5 bg-compendia-greige-60 rounded-full overflow-hidden">
                <div
                    className={`h-1.5 rounded-full transition-[width] duration-500 ease-out ${
                        isCompleted ? "bg-chameleon-green" : "bg-chameleon-yellow"
                    }`}
                    style={{ width: `${progressPercent}%` }}
                />
            </div>
            <span className="text-xs text-compendia-muted">
                {completedTasks}/{totalTasks} {t("common.tasks").toLowerCase()}
            </span>
        </section>
    );
}

function FormDetailSidebar({
    children,
    actions,
}: {
    children: React.ReactNode;
    actions?: React.ReactNode;
}) {
    const { t } = useTranslation();

    return (
        <aside className="bg-surface-subtle flex flex-col border-t border-compendia-border md:border-t-0 md:border-l md:min-h-0">
            <div className="md:flex-1 md:min-h-0 md:overflow-y-auto p-6 flex flex-col gap-4">
                <h3 className="text-label font-medium uppercase tracking-wider text-text-secondary">
                    {t("common.details")}
                </h3>
                {children}
            </div>
            {actions && (
                <div className="border-t border-compendia-border bg-surface-subtle p-6 flex flex-wrap justify-end gap-2">
                    {actions}
                </div>
            )}
        </aside>
    );
}
